const delay = (ms: number): Promise<void> =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

class RetryExhaustedError extends Error {
	constructor(public cause: unknown) {
		super('Request failed after all retries');
		this.name = 'RetryExhaustedError';
	}
}

const MAX_RETRIES = 5;
const INITIAL_BACKOFF = 800;
const RECOVERY_DELAY = 10000;
const POLL_INTERVAL_MS = 0;

/**
 * Waits until the condition is satisfied.
 */
export async function waitForBackendIdle<T>(
	url: string,
	options: {
		isIdle: (data: T) => boolean;
		onProgress?: (data: T) => void;
		onError?: (error: unknown, context: 'transient' | 'final') => void;
		fetchInit?: Omit<RequestInit, 'signal'>;
		parseResponse?: (response: Response) => Promise<T>;
		pollIntervalMs?: number;
		maxRetries?: number;
		initialBackoffMs?: number;
		recoveryDelayMs?: number; // after exhausted retries, default 10000
	}
): Promise<T> {
	const controller = new AbortController();

	const fetchWithRetry = async (
		retriesLeft: number = options.maxRetries ?? MAX_RETRIES,
		backoffMs: number = options.initialBackoffMs ?? INITIAL_BACKOFF
	): Promise<T> => {
		try {
			const response = await fetch(url, {
				...options.fetchInit,
				signal: controller.signal,
				cache: 'no-store'
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			if (options.parseResponse) {
				return options.parseResponse(response);
			}
			const raw = await response.json();
			return raw as unknown as T;
		} catch (error: unknown) {
			if (
				error &&
				typeof error === 'object' &&
				'name' in error &&
				(error as { name: string }).name === 'AbortError'
			) {
				throw error;
			}

			if (retriesLeft > 0) {
				options.onError?.(error, 'transient');
				await delay(backoffMs);
				return fetchWithRetry(retriesLeft - 1, backoffMs * 2);
			}

			throw new RetryExhaustedError(error);
		}
	};

	const poll = async (): Promise<T> => {
		try {
			const data = await fetchWithRetry();

			options.onProgress?.(data);

			if (options.isIdle(data)) {
				return data;
			}

			const interval = options.pollIntervalMs ?? POLL_INTERVAL_MS;
			if (interval > 0) {
				await delay(interval);
			}

			return poll();
		} catch (error: unknown) {
			if (
				error &&
				typeof error === 'object' &&
				'name' in error &&
				(error as { name: string }).name === 'AbortError'
			) {
				throw error;
			}

			const isFinal = error instanceof RetryExhaustedError;
			options.onError?.(
				isFinal ? ((error as RetryExhaustedError).cause ?? error) : error,
				isFinal ? 'final' : 'transient'
			);

			await delay(options.recoveryDelayMs ?? RECOVERY_DELAY);
			return poll();
		}
	};

	return poll();
}
